<template>
  <section class="md:my-14 mt-5 grid gap-y-5 p-2">
    <Loader :loader="pageLoader" />

    <div class="flex items-center justify-between">
      <h4>{{ $t("labels.generic", { pt: "Turmas", en: "Classes" }) }}</h4>
      <Button
        :label="
          $t('labels.generic', { pt: 'Adicionar turma', en: 'Add class' })
        "
        class="btn-primary p-button-sm"
        icon="pi pi-plus"
        @click="go.redirectToPage('formClasses', { tab: 0 })"
        v-if="user?.role === 'ADMINISTRATOR'"
      />
    </div>

    <div v-if="!pageLoader">
      <div v-if="classes?.length !== 0">
        <div class="bg-white rounded-lg border border-gray-300">
          <Table
            :slotData="classes"
            :columns="columns"
            :actions="actions"
            :loading="loading"
            :filters="filters"
            :hasStatus="true"
            :params="params"
            :hasActions="user?.role ==='ADMINISTRATOR'"
            @onFilter="filterFor()"
            @onClearFilter="clearFilter()"
            @onToggleFilter="(e, type) => toggleFilter(e, type)"
            @onEdit="(e) => goTo(e)"
            @onChangeStatus="(e) => confirmModal(e)"
            @onPaginate="paginate($event)"
          >
            <template #filters>
              <InputText v-if="filterType === 'name'" v-model="filters.name" />
            </template>
          </Table>
        </div>
      </div>
    </div>

    <EmptyData
      v-if="!loading && classes && classes.length === 0"
      :data="!classes || classes.length == 0"
      :label="emptyState.description"
      :image="emptyState.image"
      imgSize="h-40 md:h-64 mt-20"
      msgSize="md:w-3/12"
    />

    <ConfirmDialog :style="{ width: '80vw', maxWidth: '700px' }" />
  </section>
</template>

<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { ClassesRest } from "@/services/rest/classes.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { Messages } from "@/utils/messages.util";
import { removeItemSessionStorage } from "@/utils/save-storage.util";
import { ToastFactory } from "@/utils/toast-factory.util";
import { debounceTime, delay } from "rxjs";
import { defineComponent } from "vue";
import { ClassesService, Status } from "./classes.service";
import { actions, columns } from "./classes.utils";
import { useAuthStore } from "@/store/auth";

export default defineComponent({
  name: "classes",
  computed: {
    rest(): ClassesRest {
      return new ClassesRest();
    },
    service(): ClassesService {
      return new ClassesService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  data() {
    return {
      columns: columns as any,
      loading: true as boolean,
      pageLoader: true as boolean,
      classes: [] as any,
      searchFor: "" as string,
      actions: actions as any,
      filters: {
        isCleareable: false,
        name: null,
      } as any,
      filterType: "" as string,
      params: new QueryParams(),
      emptyState: {
        image: "empty_courses.svg",
        description: this.$t("labels.generic", {
          pt: "Você ainda não adicionou nenhuma turma clique em “Adicionar turma”.",
          en: "You haven't added any classes yet, click on “Add class”.",
        }),
      } as any,
      user: useAuthStore?.data,
    };
  },
  mounted() {
    this.classesPaginated();
    removeItemSessionStorage("classData");
  },
  methods: {
    goTo(classes?: any): void {
      this.go.redirectToPage("formClasses", { id: classes.id, tab: 0 });
    },
    changeStatus(event): void {
      this.loading = true;
      const data = {
        disabled: event.status.value === Status.ACTIVE ? true : false,
      };
      this.rest
        .updateClassStatus(event.id, data)
        .pipe(delay(500))
        .subscribe({
          next: (response) => {
            this.loading = false;
            var index = this.classes.findIndex((e: any) => e.id == response.id);
            this.classes[index].status = this.service.remakeObjectStatus(
              response.disabled ? Status.INACTIVE : Status.ACTIVE
            );
            this.$toast.add(
              ToastFactory.success(
                response.disabled
                  ? Messages.CLASSROOM_STATUS_INACTIVE
                  : Messages.CLASSROOM_STATUS_ACTIVE
              )
            );
          },
        });
    },
    toggleFilter(event: any, type: string): void {
      this.filterType = type;
    },
    filterFor(): void {
      this.filters.isCleareable = this.isCleareable();
      var params = new QueryParams();
      params.name = this.filters.name;
      this.setPage(params);
    },
    clearFilter(): void {
      this.filters.isCleareable = false;
      this.filters.name = "";
      this.classesPaginated();
    },
    isCleareable(): boolean {
      return this.filters.name !== "";
    },
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      this.setPage(params);
    },
    setPage(queryParams: QueryParams): void {
      this.pageLoader = true;
      this.service.classes.pipe(debounceTime(500)).subscribe({
        next: (response: any) => {
          this.classes = response.content;
          this.params.total = response.total;
          this.loading = false;
          this.pageLoader = false;
        },
        error:()=>{
          this.loading = false;
          this.pageLoader = false;
        }, 
        complete:()=>{
          this.loading = false;
          this.pageLoader = false;
        }
      });
      this.service.getAllClasses(queryParams);
    },
    classesPaginated(): void {
      const page = this.params.page;
      const size = this.params.size;
      const params = new QueryParams(page, size);
      this.setPage(params);
    },
    confirmModal(event?: any): void {
      const check = event.status.value === "ACTIVE";
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: check
            ? `Ao desativar a turma “Juventude Digital”, todos os usuários e cursos associados não terão mais acesso a essa turma.`
            : `Ao ativar, todos os cursos e usuários associados voltarão a ter acesso a essa turma.`,
          en: `When deactivating the “Juventude Digital” class, all users and associated courses will no longer have access to this class.`,
        }),
        header: this.$t("labels.generic", {
          pt: check ? "Desativar turma?" : "Ativar turma?",
          en: check ? "Disable class?" : "Enable class?",
        }),
        acceptLabel: check ? "Desativar turma" : "Ativar turma",
        rejectLabel: "Cancelar",
        rejectClass: "btn-outline",
        acceptClass: check ? "btn-delete-block" : "btn-primary",
        accept: () => {
          this.changeStatus(event);
        },
      });
    },
  },
});
</script>
